import { getCurrentUTCTimeZone } from 'src/utilities/DateTimeUtilities';
import { LineAppDataSeries, PieChartDataSeries, TicketsParsedDataItem } from '../../tickets/TicketsInterfaces';
import { BusinessReportingTicketAggregatedData } from '../BusinessReportingInterfaces';
import { getTopNItems } from './BusinessReportingTicketsAggregatorUtils';
import { logger } from 'src/logger';
import { OEEmptyStringMappings } from '../../FinTechOpsConstants';
import { TICKET_STATUS } from '../BusinessReportingConstants';
import { formatTimeValue } from './BusinessReportingTicketsAggregatorUtils';
/**
 * Gets top 5 resolution types by ticket count across selected duration
 * @param aggregatedData - Aggregated business reporting data
 * @returns Top 5 resolution types by ticket count in PieChartDataSeries format
 */
export const getTop5ResolutionTypesByTicketCount = (aggregatedData: BusinessReportingTicketAggregatedData[]): PieChartDataSeries[] => {
  try {
    const resolutionTypeCounts = new Map<string, number>();

    // Sum up counts across all months and applications
    aggregatedData.forEach((data) => {
      Object.entries(data.resolution_types).forEach(([resolutionType, count]) => {
        resolutionTypeCounts.set(resolutionType, (resolutionTypeCounts.get(resolutionType) || 0) + count);
      });
    });

    // Use optimized utility to get top 5 items
    const top5 = getTopNItems(resolutionTypeCounts, (count) => count, 5);
    // Calculate "All Others" total
    const totalCount = Array.from(resolutionTypeCounts.values()).reduce((sum, count) => sum + count, 0);
    const top5Total = top5.reduce((sum, [, count]) => sum + count, 0);
    const otherTotal = totalCount - top5Total;

    const result = top5.map(([title, value]) => ({
      title,
      value,
      lastUpdate: getCurrentUTCTimeZone()
    }));

    // Add "Other Resolution Types" if there are remaining types
    if (otherTotal > 0) {
      result.push({
        title: 'All Others',
        value: otherTotal,
        lastUpdate: getCurrentUTCTimeZone()
      });
    }

    return result;
  } catch (error) {
    logger.error('Error getting top 5 resolution types by ticket count', error as Error);
    return [];
  }
};

/**
 * Gets top 5 resolution types by time spent (resolved tickets only) across selected duration
 * @param filteredTicketData - Filtered ticket data
 * @returns Top 5 resolution types by time spent in PieChartDataSeries format
 */
export const getTop5ResolutionTypesByTimeSpent = (filteredTicketData: TicketsParsedDataItem[]): PieChartDataSeries[] => {
  try {
    if (!Array.isArray(filteredTicketData)) {
      throw new Error('Invalid data: filteredTicketData must be an array');
    }
    // Validate that the data has the expected structure
    if (
      filteredTicketData.length > 0 &&
      filteredTicketData.some(
        (ticket) => typeof ticket !== 'object' || ticket === null || (!ticket.hasOwnProperty('status') && !ticket.hasOwnProperty('total_time_spent'))
      )
    ) {
      throw new Error('Invalid data: filteredTicketData contains invalid ticket objects');
    }

    const resolutionTypeTimeSpent = new Map<string, number>();

    // Filter only resolved tickets and sum time spent by resolution type
    filteredTicketData
      .filter((ticket) => ticket.status === TICKET_STATUS.RESOLVED && ticket.total_time_spent > 0)
      .forEach((ticket) => {
        const resolutionType = ticket.resolution_type || OEEmptyStringMappings.Not_Entered;
        resolutionTypeTimeSpent.set(resolutionType, (resolutionTypeTimeSpent.get(resolutionType) || 0) + ticket.total_time_spent);
      });

    // Use optimized utility to get top 5 items
    const top5 = getTopNItems(resolutionTypeTimeSpent, (timeSpent) => timeSpent, 5);
    // Calculate "All Others" total
    const totalTimeSpent = Array.from(resolutionTypeTimeSpent.values()).reduce((sum, time) => sum + time, 0);
    const top5Total = top5.reduce((sum, [, time]) => sum + time, 0);
    const otherTotal = totalTimeSpent - top5Total;

    const result = top5.map(([title, value]) => ({
      title,
      value: formatTimeValue(value),
      lastUpdate: getCurrentUTCTimeZone()
    }));

    // Add "Other Resolution Types" if there are remaining types
    if (otherTotal > 0) {
      result.push({
        title: 'All Others',
        value: formatTimeValue(otherTotal),
        lastUpdate: getCurrentUTCTimeZone()
      });
    }

    return result;
  } catch (error) {
    logger.error('Error getting top 5 resolution types by time spent', error as Error);
    return [];
  }
};

/**
 * Filters application chart data to show only top 5 applications by total ticket count
 * Uses O(n) algorithm to find top 5 without full sorting
 * @param applicationData - Application chart data series
 * @returns Top 5 applications by ticket count
 */
export const getTop5ApplicationChartData = (applicationData: LineAppDataSeries[]): LineAppDataSeries[] => {
  try {
    // Creating a map for topNItems because it accepts the key to be string
    const seriesMap = new Map<string, LineAppDataSeries>();
    applicationData.forEach((series, index) => {
      const key = series.title || `Series_${index}`;
      seriesMap.set(key, series);
    });

    const top5 = getTopNItems(seriesMap, (series) => series.data.reduce((sum, point) => sum + point.y, 0), 5);
    // Return just the series objects for chart data
    return top5.map(([, series]) => series);
  } catch (error) {
    logger.error('Error getting top 5 application chart data', error as Error);
    return [];
  }
};
